import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

export default function DuplicatePhotosModal({ isOpen, onClose }) {
    const [duplicates, setDuplicates] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const checkDuplicates = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await apiService.checkDuplicatePhotos();
            setDuplicates(data);
        } catch (err) {
            setError(err.message || '检测失败');
        } finally {
            setIsLoading(false);
        }
    };

    // 自动检测当打开modal时
    useEffect(() => {
        if (isOpen && !duplicates) {
            checkDuplicates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (!isOpen) return null;

    const exportToCSV = () => {
        if (!duplicates || duplicates.duplicates.length === 0) return;

        // 生成CSV数据
        const headers = ['文件路径', '文件名', '重复次数', '人才列表'];
        const rows = duplicates.duplicates.map(dup => [
            dup.filePath,
            dup.fileName,
            dup.duplicateCount,
            dup.talents.map(t => t.talentName).join(' | ')
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // 下载文件
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `重复照片报告_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
                {/* 标题栏 */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold flex items-center">
                        <span className="mr-2">🔍</span>
                        重复照片检测
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 text-2xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* 内容区域 */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {/* 加载状态 */}
                    {isLoading && (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                            <p className="mt-4 text-gray-600">正在检测重复照片...</p>
                        </div>
                    )}

                    {/* 错误状态 */}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 结果显示 */}
                    {!isLoading && !error && duplicates && (
                        <>
                            {/* 摘要卡片 */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                                    <div className="text-sm text-blue-600 font-medium">重复文件数</div>
                                    <div className="text-3xl font-bold text-blue-700 mt-2">
                                        {duplicates.totalDuplicateFiles}
                                    </div>
                                </div>

                                <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-500">
                                    <div className="text-sm text-orange-600 font-medium">影响记录数</div>
                                    <div className="text-3xl font-bold text-orange-700 mt-2">
                                        {duplicates.totalAffectedRecords}
                                    </div>
                                </div>

                                <div className={`rounded-lg p-4 border-l-4 ${
                                    duplicates.totalDuplicateFiles === 0
                                        ? 'bg-green-50 border-green-500'
                                        : 'bg-red-50 border-red-500'
                                }`}>
                                    <div className={`text-sm font-medium ${
                                        duplicates.totalDuplicateFiles === 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        系统状态
                                    </div>
                                    <div className={`text-3xl font-bold mt-2 ${
                                        duplicates.totalDuplicateFiles === 0 ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                        {duplicates.totalDuplicateFiles === 0 ? '✅ 正常' : '⚠️ 异常'}
                                    </div>
                                </div>
                            </div>

                            {/* 无重复提示 */}
                            {duplicates.totalDuplicateFiles === 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                                    <div className="text-6xl mb-4">✅</div>
                                    <h3 className="text-xl font-bold text-green-800 mb-2">太好了！</h3>
                                    <p className="text-green-600">未发现重复照片，系统运行正常。</p>
                                </div>
                            )}

                            {/* 重复列表 */}
                            {duplicates.totalDuplicateFiles > 0 && (
                                <>
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-bold text-gray-800">重复照片详情</h3>
                                        <button
                                            onClick={exportToCSV}
                                            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center"
                                        >
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            导出CSV
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {duplicates.duplicates.map((dup, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                                                {/* 文件信息头部 */}
                                                <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center">
                                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mr-3 ${
                                                                dup.duplicateCount > 10
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : dup.duplicateCount > 5
                                                                    ? 'bg-orange-100 text-orange-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                            }`}>
                                                                {dup.duplicateCount} 个人才使用
                                                            </span>
                                                            <span className="text-gray-600 text-sm">
                                                                {(dup.fileSize / 1024).toFixed(2)} KB
                                                            </span>
                                                        </div>
                                                        <div className="text-sm font-medium text-gray-900 mt-2">
                                                            📄 {dup.fileName}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1 font-mono">
                                                            {dup.filePath}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 使用该照片的人才列表 */}
                                                <div className="p-4">
                                                    <div className="text-sm font-medium text-gray-700 mb-2">
                                                        使用该照片的人才：
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                                        {dup.talents.map((talent, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="bg-white border border-gray-200 rounded px-3 py-2 text-sm"
                                                            >
                                                                <div className="font-medium text-gray-900">
                                                                    {talent.talentName || '未知'}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {new Date(talent.uploadedAt).toLocaleDateString('zh-CN')}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* 建议操作 */}
                                    <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                                        <h4 className="text-sm font-bold text-blue-800 mb-2">💡 建议操作：</h4>
                                        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                                            <li>通知相关用户重新上传正确的照片</li>
                                            <li>删除重复的照片记录（保留最早上传的）</li>
                                            <li>系统已自动修复，未来不会再出现此问题</li>
                                        </ul>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* 底部按钮 */}
                <div className="bg-gray-50 px-6 py-4 flex justify-between">
                    <button
                        onClick={checkDuplicates}
                        disabled={isLoading}
                        className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                检测中...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                重新检测
                            </>
                        )}
                    </button>

                    <button
                        onClick={onClose}
                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                    >
                        关闭
                    </button>
                </div>
            </div>
        </div>
    );
}
